import { createClient } from '@supabase/supabase-js';

// Values baked in at build time by Vite. On Netlify, set these via
// Site configuration → Environment variables (VITE_ prefix required so
// they're exposed to the browser bundle).
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

// These are the placeholders that ship in .env for local development.
// If either variable is missing, empty, or still one of these, we treat
// the app as running in demo mode and skip creating a real client so
// nothing tries to reach a dead URL.
const PLACEHOLDER_URLS = new Set([
  'https://dummy.supabase.co',
  'https://your-project.supabase.co',
]);

const PLACEHOLDER_KEYS = new Set([
  'dummykey.updateyourkkey.here',
  'your-anon-key-here',
]);

function looksLikeRealUrl(value) {
  if (!value || typeof value !== 'string') return false;
  if (PLACEHOLDER_URLS.has(value)) return false;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.hostname.endsWith('.supabase.co');
  } catch {
    return false;
  }
}

function looksLikeRealKey(value) {
  if (!value || typeof value !== 'string') return false;
  if (PLACEHOLDER_KEYS.has(value)) return false;

  // Supabase supports two client-key formats. Accept either.
  //
  //   1. New "publishable" keys — prefixed `sb_publishable_` — introduced in
  //      the 2025 API-keys migration. Safe for browser use when RLS is on.
  //   2. Legacy "anon public" keys — JWTs of the form <header>.<payload>.<sig>
  //      with base64url segments. Older projects still ship these.
  //
  // The `sb_secret_` and legacy `service_role` keys are deliberately not
  // recognized here — those bypass RLS and must never be shipped in a
  // browser bundle.
  if (value.startsWith('sb_publishable_') && value.length > 20) return true;

  return value.split('.').length === 3 && value.length > 40;
}

// ---------------------------------------------------------------------------
// Demo-mode URL override.
//
// One deployed site can serve both audiences: paying customers hit the root
// URL and get live mode, sales prospects hit `?demo=1` (or `?mode=demo`) and
// get demo mode against localStorage even when real Supabase creds are baked
// into the build. The choice sticks in sessionStorage for the tab so
// navigating between pages doesn't need the query param on every link.
//
// `?live=1` explicitly clears the override, so a sales rep can flip a real
// customer's browser back into live mode without closing the tab.
// ---------------------------------------------------------------------------

const DEMO_OVERRIDE_KEY = 'edutracker.forceDemoMode.v1';

function readDemoOverride() {
  if (typeof window === 'undefined') return false;

  try {
    const params = new URLSearchParams(window.location.search);
    const modeParam = (params.get('mode') || '').toLowerCase();
    if (params.has('demo') || modeParam === 'demo') {
      try {
        window.sessionStorage.setItem(DEMO_OVERRIDE_KEY, '1');
      } catch {
        // sessionStorage disabled (Safari private mode etc.); the URL param
        // still forces demo for this render.
      }
      return true;
    }
    if (params.has('live') || modeParam === 'live') {
      try {
        window.sessionStorage.removeItem(DEMO_OVERRIDE_KEY);
      } catch {
        // ignore
      }
      return false;
    }
  } catch {
    // URL parsing failed — fall through to sessionStorage check.
  }

  try {
    return window.sessionStorage.getItem(DEMO_OVERRIDE_KEY) === '1';
  } catch {
    return false;
  }
}

export function isDemoModeForced() {
  return readDemoOverride();
}

export function clearDemoOverride() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(DEMO_OVERRIDE_KEY);
  } catch {
    // ignore
  }
}

// Explicitly flip the app into demo mode for the current session (no
// URL param dance required). Used by the login page's "Try the app"
// one-tap buttons so a district evaluator's first tap always succeeds:
// on a live-Supabase deployment we deliberately DON'T call
// signInWithPassword with canned fake credentials (which would fail and
// throw an ugly error) — we bypass Supabase entirely and give them the
// localStorage-only demo experience, with the DemoModeBanner making it
// obvious they're in demo mode and can flip back to live with one click.
export function setForceDemoMode() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(DEMO_OVERRIDE_KEY, '1');
  } catch {
    // sessionStorage disabled — caller is expected to also pass ?demo=1
    // in the URL, which readDemoOverride() honors as a fallback.
  }
}

export function isSupabaseConfigured() {
  if (readDemoOverride()) return false;
  return looksLikeRealUrl(SUPABASE_URL) && looksLikeRealKey(SUPABASE_ANON_KEY);
}

// Distinguishes "no creds at all" (real demo — no Supabase project exists)
// from "creds exist, we're just forcing demo via URL". UI can use this to
// show a helpful "Exit demo mode" hint that clearDemoOverride() acts on.
export function hasRealSupabaseCreds() {
  return looksLikeRealUrl(SUPABASE_URL) && looksLikeRealKey(SUPABASE_ANON_KEY);
}

// Create the client based only on whether real creds exist, not on the
// demo-mode override. That way toggling `?demo=1` mid-session doesn't need
// to reload the module: consumers gate on `isSupabaseConfigured()`, and if
// they see false they just skip using this client.
export const supabase = hasRealSupabaseCreds()
  ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
      },
    })
  : null;

export const supabaseConfig = {
  url: SUPABASE_URL || null,
  hasAnonKey: Boolean(SUPABASE_ANON_KEY),
  configured: isSupabaseConfigured(),
};

// ---------------------------------------------------------------------------
// Schema-error detection
//
// PostgREST returns two distinctive errors when a query hits a missing
// table or column. The UI needs to distinguish these from generic
// "something went wrong" errors so we can:
//   * degrade gracefully (Team Hub → "setup pending" state instead of a
//     scary red banner),
//   * surface a one-click "here's the SQL you need to run" prompt via
//     the app-wide SchemaSetupBanner, and
//   * avoid retrying forever (a missing table won't fix itself; a network
//     blip will).
//
// Error shapes we recognize:
//
//   PGRST205 { code: 'PGRST205', message: "Could not find the table
//             'public.user_profiles' in the schema cache" }
//   PGRST204 { code: 'PGRST204', message: "Could not find the
//             'academic_level' column of 'students' in the schema cache" }
//   42P01    Postgres "relation ... does not exist" (rarer path, but real)
//
// The message text isn't stable across PostgREST versions, so we key off
// codes first and fall back to a permissive regex.
// ---------------------------------------------------------------------------

const MISSING_TABLE_CODES  = new Set(['PGRST205', '42P01']);
const MISSING_COLUMN_CODES = new Set(['PGRST204', 'PGRST103', '42703']);

function extractSingleQuoted(msg) {
  if (!msg) return null;
  const m = /'([^']+)'/.exec(String(msg));
  return m ? m[1] : null;
}

// Collect every text-like string we can find in the error object. In
// practice PostgREST/Supabase-JS returns errors in a fairly consistent
// shape, but the exact shape has drifted between versions and different
// call sites wrap it differently — sometimes we get { code, message },
// sometimes { error: { code, message } }, sometimes { data: null,
// error: { … } }, and sometimes a plain string. This helper flattens
// all of that into one giant "look for schema-cache clues anywhere"
// string so a stale bundle, a wrapper, or an untyped rethrow never
// prevents classification.
function collectErrorText(err) {
  if (!err) return '';
  if (typeof err === 'string') return err;
  const bits = [
    err.message,
    err.details,
    err.hint,
    err.description,
    err.error_description,
    err?.error?.message,
    err?.error?.details,
    err?.error?.hint,
    err?.data?.message,
    err?.body?.message,
    err?.body,
  ];
  return bits.filter((b) => typeof b === 'string').join(' | ');
}

function collectErrorCode(err) {
  if (!err) return '';
  return String(
    err.code ||
      err?.error?.code ||
      err?.body?.code ||
      err?.data?.code ||
      err.status ||
      err?.error?.status ||
      '',
  );
}

// Bulletproof classifier. Returns { kind: 'missing_table' | 'missing_column' }
// or null. Never throws. Recognizes:
//
//   - PostgREST error codes:  PGRST205 (missing table), PGRST204 /
//     PGRST103 (missing column)
//   - Postgres error codes:   42P01 (missing table), 42703 (missing column)
//   - Message regexes:        "Could not find the table…",
//     "relation … does not exist", "Could not find the … column",
//     "column … does not exist", and — as a last-resort catch-all —
//     any mention of "schema cache" (only PostgREST's schema-cache
//     errors say that phrase, so it's safe as a fallback).
export function classifySupabaseError(err) {
  if (!err) return null;
  const code = collectErrorCode(err);
  const msg  = collectErrorText(err);

  const isMissingTable =
    MISSING_TABLE_CODES.has(code) ||
    /Could not find the table/i.test(msg) ||
    /relation .* does not exist/i.test(msg);

  const isMissingColumn =
    MISSING_COLUMN_CODES.has(code) ||
    /Could not find the .* column/i.test(msg) ||
    /column .* does not exist/i.test(msg);

  if (isMissingTable) {
    const tableRef = extractSingleQuoted(msg);
    return {
      kind: 'missing_table',
      table: tableRef ? tableRef.replace(/^public\./, '') : null,
      raw: err,
    };
  }

  if (isMissingColumn) {
    return {
      kind: 'missing_column',
      column: extractSingleQuoted(msg),
      raw: err,
    };
  }

  // Fallback: any mention of "schema cache" in the message is
  // PostgREST-speak for a schema-mismatch, even if the code came
  // through empty. Treat as missing_table because that's the more
  // common cause and the friendlier UI branch.
  if (/schema cache/i.test(msg)) {
    const tableRef = extractSingleQuoted(msg);
    return {
      kind: 'missing_table',
      table: tableRef ? tableRef.replace(/^public\./, '') : null,
      raw: err,
    };
  }

  return null;
}

export function isSchemaSetupError(err) {
  const c = classifySupabaseError(err);
  return c?.kind === 'missing_table' || c?.kind === 'missing_column';
}

// ---------------------------------------------------------------------------
// "Database is unreachable" detection
//
// Distinct from schema errors (see classifySupabaseError above): this
// covers the case where the SQL is fine but we couldn't talk to the
// database at all. In special-ed teacher land, by far the most common
// cause is Supabase's free-tier auto-pause after 7 days of inactivity —
// the site starts throwing 5xx / network errors and the roster comes
// back empty, which is the single scariest failure mode for a teacher
// ("I lost all my students"). The UI uses this classifier to swap the
// generic red "Failed to load" banner for a friendly amber "your
// database is asleep, click here to wake it" banner AND to fall back
// to the last-known cached roster instead of showing an empty list.
//
// We recognize both explicit "project paused" strings from Supabase
// and the generic browser-side fetch failures that surface when the
// project is asleep or the network is down. From a teacher's
// perspective the recovery is the same in both cases: retry in a
// moment, and if it keeps failing, restore the project.
// ---------------------------------------------------------------------------

const PROJECT_PAUSED_PATTERNS = [
  /project is paused/i,
  /project is inactive/i,
  /project has been paused/i,
  /database is paused/i,
];

const NETWORK_FAILURE_PATTERNS = [
  /failed to fetch/i,
  /networkerror/i,
  /network error/i,
  /load failed/i,
  /unable to connect/i,
  /fetch failed/i,
  /err_network/i,
  /err_internet_disconnected/i,
];

// True if the error text explicitly says the Supabase project is paused.
export function isProjectPausedError(err) {
  const text = collectErrorText(err);
  return PROJECT_PAUSED_PATTERNS.some((r) => r.test(text));
}

// True if the error is either an explicit pause OR a generic
// unreachable-database failure (network / fetch). We treat both the
// same in the UI because the teacher-facing recovery is identical:
// show the cached roster, tell them the DB is asleep, give them the
// dashboard link. If it turns out to be network the "Try again"
// button just works once they're back online.
export function isDatabaseUnreachableError(err) {
  if (!err) return false;
  if (isProjectPausedError(err)) return true;
  const text = collectErrorText(err);
  const code = String(collectErrorCode(err));
  if (NETWORK_FAILURE_PATTERNS.some((r) => r.test(text))) return true;
  // 5xx from Supabase's edge is the shape a paused project usually
  // takes when Supabase's own error text doesn't propagate cleanly.
  // 502/503/504 are all "upstream isn't answering" from the teacher's
  // perspective. 500 alone we deliberately DON'T treat as pause —
  // that's usually a legitimate query error worth surfacing.
  if (code === '502' || code === '503' || code === '504') return true;
  return false;
}

// ---------------------------------------------------------------------------
// Migration registry
//
// Which migration a given missing-table / missing-column error points to,
// so the UI can name the exact SQL file the user needs to run. Keys are
// the strings we look for in the PostgREST error message.
//
// This is a source of truth for the SchemaSetupBanner and the Team Hub
// "setup pending" state — both display "run migrations X, Y, Z" and
// both link to the bundled APPLY_ALL_MIGRATIONS.sql file in the repo.
// ---------------------------------------------------------------------------

export const MIGRATION_REGISTRY = {
  tables: {
    // 0009 — user_profiles + attribution triggers on the existing tables
    user_profiles:            '0009_user_profiles.sql',
    // 0010 — student_teams + persisted profile detail columns on students
    student_team_members:     '0010_student_teams_and_profile_details.sql',
    // 0002 — the initial PBIS / behavior data model
    behavior_incidents:       '0002_pbis_data_model.sql',
    behavior_goals:           '0002_pbis_data_model.sql',
    behavior_support_plans:   '0002_pbis_data_model.sql',
    // 0001 — the initial students table
    students:                 '0001_students.sql',
    // 0005 — goal_progress_entries
    goal_progress_entries:    '0005_goal_progress_entries.sql',
    // 0008 — iep-dates view + dashboard snapshot
    dashboard_snapshot:       '0008_iep_dates_and_dashboard_view.sql',
  },
  columns: {
    // Persisted profile detail columns added in 0010
    emergency_contact:        '0010_student_teams_and_profile_details.sql',
    accommodations:           '0010_student_teams_and_profile_details.sql',
    secondary_disabilities:   '0010_student_teams_and_profile_details.sql',
    service_providers:        '0010_student_teams_and_profile_details.sql',
    medical_info:             '0010_student_teams_and_profile_details.sql',
    behavioral_notes:         '0010_student_teams_and_profile_details.sql',
    academic_level:           '0010_student_teams_and_profile_details.sql',
    communication_method:     '0010_student_teams_and_profile_details.sql',
    transportation_needs:     '0010_student_teams_and_profile_details.sql',
    dietary_restrictions:     '0010_student_teams_and_profile_details.sql',
    // IEP review-date columns added in 0008
    iep_annual_review_date:   '0008_iep_dates_and_dashboard_view.sql',
    iep_reevaluation_date:    '0008_iep_dates_and_dashboard_view.sql',
    // 0011 — incident-to-goal linkage array on behavior_incidents
    linked_goal_ids:          '0011_link_incidents_to_goals.sql',
    source:                   '0012_student_self_checkins.sql',
    student_rating:           '0012_student_self_checkins.sql',
    feeling:                  '0012_student_self_checkins.sql',
  },
};

// Given a classified error, return { migrationFile, target } or null.
export function migrationForSchemaError(err) {
  const c = classifySupabaseError(err);
  if (!c) return null;
  if (c.kind === 'missing_table' && c.table) {
    const f = MIGRATION_REGISTRY.tables[c.table];
    if (f) return { migrationFile: f, target: c.table, kind: 'table' };
  }
  if (c.kind === 'missing_column' && c.column) {
    const f = MIGRATION_REGISTRY.columns[c.column];
    if (f) return { migrationFile: f, target: c.column, kind: 'column' };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Pub/sub — anywhere in the app can push a schema error, and any UI that
// wants to surface an "your database is missing migrations" prompt can
// subscribe. Two typical subscribers:
//   * SchemaSetupBanner    (top of app, always visible until dismissed)
//   * Team Hub error panel (inline on the page that triggered it)
// ---------------------------------------------------------------------------

const schemaErrorListeners = new Set();
let latestSchemaError = null;

export function reportSchemaError(err, context = null) {
  const c = classifySupabaseError(err);
  if (!c) return null;
  const entry = {
    kind: c.kind,
    target: c.table || c.column || null,
    migration: migrationForSchemaError(err)?.migrationFile || null,
    context,
    seenAt: Date.now(),
  };
  latestSchemaError = entry;
  for (const fn of schemaErrorListeners) {
    try { fn(entry); } catch { /* subscriber threw; keep the rest alive */ }
  }
  return entry;
}

export function subscribeSchemaErrors(fn) {
  if (typeof fn !== 'function') return () => {};
  schemaErrorListeners.add(fn);
  if (latestSchemaError) {
    try { fn(latestSchemaError); } catch { /* ignore */ }
  }
  return () => schemaErrorListeners.delete(fn);
}

export function getLatestSchemaError() {
  return latestSchemaError;
}
